/**
 * DayCloseScreen — Session summary, cash reconciliation, and Z-report print.
 *
 * Mirrors the BigxBook DayCloseScreen business logic:
 *   • Loads all invoices for the active billing session (or today as fallback)
 *   • Groups payments by method (CASH/UPI/CARD/OTHER)
 *   • Loads cash expenses for the session date
 *   • Renders cash reconciliation form (actualCash input)
 *   • "Close Day" → closeSession(actualCash) with confirmation dialog
 *   • "Print Z-Report" → roll-width page, browser print dialog (printZReport)
 *   • "Export Summary" → text summary to clipboard
 */

import { useFeedbackPopup } from '@/hooks/useFeedbackPopup';
import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useCallback, useEffect, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Printer, Copy, Check, AlertTriangle, ClipboardList } from 'lucide-react';
import { database } from '@/db';
import { Invoice, Payment, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Expense } from '@/db/models/System';
import Store from '@/db/models/Store';
import { useBillingSession } from '@/billing/useBillingSession';
import { useAppStore } from '@/store/useAppStore';
import { usePermissions } from '@/rbac/usePermissions';
import { REPORTS_DAY_CLOSE } from '@/rbac/permissions';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { startOfDay, startOfNextDay } from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { printZReport, type ZReportData } from '@/printing/zReport';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DayData {
  totalSales: number;
  invoiceCount: number;
  cashSales: number;
  upiSales: number;
  cardSales: number;
  otherSales: number;
  creditDue: number;
  cashExpenses: number;
  openingCash: number;
  expectedCash: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DayCloseScreen() {
  const { can } = usePermissions();
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const {
    activeSession,
    isLoading: sessionLoading,
    isSessionOpen,
    closeSession,
    getSessionSummary,
  } = useBillingSession();

  const [dayData, setDayData]     = useState<DayData | null>(null);
  const [loading, setLoading]     = useState(true);
  const [actualCash, setActualCash] = useState('');
  const [closingState, setClosingState] = useState<'idle' | 'confirming' | 'closing' | 'closed'>('idle');
  const [closeError, setCloseError] = useState('');
  useErrorPopup(closeError, () => setCloseError(''));
  const [printing, setPrinting]   = useState(false);
  const [printResult, setPrintResult] = useState<{ ok: boolean; message?: string } | null>(null);
  useFeedbackPopup(
    printResult && (printResult.ok ? 'Z-Report sent to the printer.' : `Z-Report did not print: ${printResult.message ?? 'check the printer.'}`),
    printResult?.ok ? 'success' : 'error',
    () => setPrintResult(null),
  );
  const [copied, setCopied]       = useState(false);

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      // Expense window must match the invoice window, or the drawer won't
      // balance. An open session can straddle midnight (opened Fri 18:00, still
      // running Sat), and its invoices are session-scoped rather than day-scoped
      // — so span from the session's opening day through today, not just the
      // day it opened. With no session, both windows are simply today.
      const dayStart = startOfDay(activeSession?.sessionDate ?? new Date()).getTime();
      const dayEnd = startOfNextDay(new Date()).getTime();

      // Invoices: prefer session-scoped, fall back to today
      let invoices: Invoice[];
      if (activeSession) {
        invoices = await database.get<Invoice>('invoices').query(
          Q.where('billing_session_id', activeSession.id),
          Q.where('is_deleted', false),
          Q.where('type', 'SALE'),
          Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
        ).fetch();
      } else {
        invoices = await database.get<Invoice>('invoices').query(
          Q.where('store_id', activeStoreId),
          Q.where('is_deleted', false),
          Q.where('type', 'SALE'),
          Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
          Q.where('created_at', Q.gte(dayStart)),
          Q.where('created_at', Q.lt(dayEnd)),
        ).fetch();
      }

      const invoiceIds = invoices.map((i) => i.id);

      // Payments batch
      const allPayments: Payment[] = [];
      for (let i = 0; i < invoiceIds.length; i += 500) {
        const batch = await database.get<Payment>('payments').query(
          Q.where('invoice_id', Q.oneOf(invoiceIds.slice(i, i + 500))),
          Q.where('is_deleted', false),
        ).fetch();
        allPayments.push(...batch);
      }

      const paySum = (method: string) =>
        allPayments.filter((p) => p.method === method).reduce((s, p) => s + p.amount, 0);

      const cashSales = paySum('CASH');
      const upiSales  = paySum('UPI');
      const cardSales = paySum('CARD');
      const otherSales = allPayments
        .filter((p) => !['CASH', 'UPI', 'CARD'].includes(p.method))
        .reduce((s, p) => s + p.amount, 0);

      // Cash expenses for the session's day. Keyed on `expense_date` (when the
      // cash actually left the drawer), not `created_at` (when the row was
      // typed in) — otherwise a back-dated expense is charged against today and
      // the variance lands on the wrong cashier.
      const expenses = await database.get<Expense>('expenses').query(
        Q.where('store_id', activeStoreId),
        Q.where('is_deleted', false),
        Q.where('expense_date', Q.gte(dayStart)),
        Q.where('expense_date', Q.lt(dayEnd)),
      ).fetch();
      const cashExpenses = expenses
        .filter((e) => !e.paymentMethod || e.paymentMethod === 'CASH')
        .reduce((s, e) => s + e.amount, 0);

      const openingCash = activeSession?.openingCash ?? 0;
      const expectedCash = openingCash + cashSales - cashExpenses;

      setDayData({
        totalSales: invoices.reduce((s, inv) => s + inv.totalAmount, 0),
        invoiceCount: invoices.length,
        cashSales,
        upiSales,
        cardSales,
        otherSales,
        creditDue: invoices.reduce((s, inv) => s + inv.dueAmount, 0),
        cashExpenses,
        openingCash,
        expectedCash,
      });
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, activeSession]);

  useEffect(() => { void load(); }, [load]);

  const actualCashNum = parseFloat(actualCash.replace(/,/g, '')) || 0;
  const variance = dayData ? actualCashNum - dayData.expectedCash : 0;

  const handleClose = async () => {
    if (!isSessionOpen || !dayData || actualCash.trim() === '') return;
    setClosingState('closing');
    setCloseError('');
    try {
      await closeSession(actualCashNum);
      setClosingState('closed');
      notifySuccess('Day closed.');
    } catch (err) {
      setCloseError((err as Error).message);
      setClosingState('idle');
    }
  };

  const handlePrintZ = async () => {
    if (!dayData || !activeStoreId) return;
    setPrinting(true);
    setPrintResult(null);
    try {
      let store: Store | null = null;
      try { store = await database.get<Store>('stores').find(activeStoreId); } catch { /* noop */ }

      const now = new Date();
      const zData: ZReportData = {
        storeName: store?.name ?? 'Store',
        storeAddress: store?.address ?? undefined,
        reportDate: formatDate(now),
        reportTime: formatTime(now),
        openingCash: dayData.openingCash,
        cashSales:   dayData.cashSales,
        upiSales:    dayData.upiSales,
        cardSales:   dayData.cardSales,
        otherSales:  dayData.otherSales,
        creditDue:   dayData.creditDue,
        totalSales:  dayData.totalSales,
        invoiceCount: dayData.invoiceCount,
        cashExpenses: dayData.cashExpenses,
        expectedCash: dayData.expectedCash,
        actualCash:  actualCashNum,
        variance,
      };
      const result = await printZReport(zData);
      setPrintResult(result);
    } finally {
      setPrinting(false);
    }
  };

  const handleCopyText = () => {
    if (!dayData) return;
    const lines = [
      `Day Close Summary — ${formatDate(new Date())}`,
      `Total Sales: ${formatCurrency(dayData.totalSales)} (${dayData.invoiceCount} invoices)`,
      `Cash: ${formatCurrency(dayData.cashSales)} | UPI: ${formatCurrency(dayData.upiSales)} | Card: ${formatCurrency(dayData.cardSales)}`,
      dayData.creditDue > 0 ? `Credit Due: ${formatCurrency(dayData.creditDue)}` : '',
      `Opening Cash: ${formatCurrency(dayData.openingCash)}`,
      `Expected Cash: ${formatCurrency(dayData.expectedCash)}`,
      actualCash ? `Actual Cash: ${formatCurrency(actualCashNum)} | ${variance >= 0 ? 'Overage' : 'Short'}: ${formatCurrency(Math.abs(variance))}` : '',
    ].filter(Boolean).join('\n');
    void navigator.clipboard.writeText(lines);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!can(REPORTS_DAY_CLOSE)) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="text-heading text-fg-primary">Access restricted</div>
          <div className="mt-1 text-body text-fg-tertiary">You don't have permission to view Day Close.</div>
        </div>
      </div>
    );
  }

  if (sessionLoading || loading) {
    return <div className="flex h-full items-center justify-center text-body text-fg-tertiary">Loading…</div>;
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Session status banner */}
      <div className={cn(
        'rounded-lg px-4 py-3 flex items-center gap-3',
        closingState === 'closed'
          ? 'bg-success/10 text-success-ink'
          : isSessionOpen
            ? 'bg-success/10 text-success-ink'
            : 'bg-bg-subtle text-fg-secondary',
      )}>
        <div className="flex-1">
          {closingState === 'closed' ? (
            <span className="font-medium">Day closed successfully.</span>
          ) : isSessionOpen ? (
            <span className="font-medium">Day session is OPEN</span>
          ) : (
            <span>No active session — showing today's activity.</span>
          )}
          {activeSession && closingState !== 'closed' && (
            <span className="ml-2 text-body opacity-75">Opened {formatTime(activeSession.openedAt)}</span>
          )}
        </div>
        {isSessionOpen && closingState !== 'closed' && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm"
              onClick={() => void handleCopyText()} disabled={!dayData}>
              {copied ? <Check size={14} className="text-success-ink" /> : <Copy size={14} />}
              <span className="ml-1.5">Copy Summary</span>
            </Button>
            <Button variant="secondary" size="sm"
              onClick={() => void handlePrintZ()} disabled={printing || !dayData}>
              <Printer size={14} className="mr-1.5" />
              {printing ? 'Printing…' : 'Print Z-Report'}
            </Button>
          </div>
        )}
      </div>


      {dayData && (
        <>
          {/* Sales summary */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Total sales"    value={formatCurrency(dayData.totalSales)} accent />
            <KPI label="Invoices"       value={String(dayData.invoiceCount)} />
            <KPI label="Cash sales"     value={formatCurrency(dayData.cashSales)} />
            <KPI label="Digital (UPI+Card)" value={formatCurrency(dayData.upiSales + dayData.cardSales)} />
          </div>

          {/* Payment breakdown */}
          <Section title="Payment breakdown">
            <PayRow label="Cash"  amount={dayData.cashSales} />
            <PayRow label="UPI"   amount={dayData.upiSales} />
            <PayRow label="Card"  amount={dayData.cardSales} />
            {dayData.otherSales > 0  && <PayRow label="Other"  amount={dayData.otherSales} />}
            {dayData.creditDue  > 0  && <PayRow label="Credit due" amount={dayData.creditDue} warn />}
          </Section>

          {/* Cash reconciliation */}
          <Section title="Cash reconciliation">
            <RecRow label="Opening cash"  value={formatCurrency(dayData.openingCash)} />
            <RecRow label="+ Cash sales"  value={formatCurrency(dayData.cashSales)} />
            {dayData.cashExpenses > 0 && (
              <RecRow label="− Cash expenses" value={`-${formatCurrency(dayData.cashExpenses)}`} />
            )}
            <RecRow label="= Expected cash" value={formatCurrency(dayData.expectedCash)} bold />

            {isSessionOpen && closingState !== 'closed' && (
              <>
                <div className="my-3 border-t border-stroke-hairline/10" />
                <div className="flex items-center gap-3">
                  <label className="min-w-32 text-body text-fg-secondary">Actual cash (₹):</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={actualCash}
                    onChange={(e) => setActualCash(e.target.value)}
                    placeholder="Enter cash in drawer"
                    className="w-40 rounded-md border border-stroke-hairline/15 bg-bg-surface px-3 py-2 text-body text-fg-primary tabular-nums outline-none focus:border-accent/40"
                  />
                </div>
                {actualCash.trim() !== '' && (
                  <div className={cn(
                    'mt-2 flex items-center gap-2 rounded-md px-3 py-2 text-body',
                    variance >= 0 ? 'bg-success/10 text-success-ink' : 'bg-warning/10 text-warning-ink',
                  )}>
                    {variance !== 0 && <AlertTriangle size={14} className="shrink-0" />}
                    <span>
                      {variance === 0
                        ? 'Cash matches — exact.'
                        : variance > 0
                          ? `Overage: ${formatCurrency(variance)}`
                          : `Short: ${formatCurrency(Math.abs(variance))}`}
                    </span>
                  </div>
                )}
              </>
            )}

            {closingState === 'closed' && activeSession?.variance != null && (
              <div className={cn(
                'mt-2 rounded-md px-3 py-2 text-body',
                (activeSession.variance ?? 0) >= 0 ? 'bg-success/10 text-success-ink' : 'bg-warning/10 text-warning-ink',
              )}>
                {activeSession.variance === 0 ? 'Cash matched exactly.' :
                 activeSession.variance > 0
                   ? `Overage: ${formatCurrency(activeSession.variance)}`
                   : `Short: ${formatCurrency(Math.abs(activeSession.variance ?? 0))}`}
              </div>
            )}
          </Section>

          {/* Close Day button */}
          {isSessionOpen && closingState !== 'closed' && (
            <div className="mt-2">
              {closingState === 'confirming' ? (
                <div className="flex items-center gap-3">
                  <span className="text-body text-fg-secondary">
                    Close today's session? This action cannot be undone.
                  </span>
                  <Button variant="danger" onClick={() => void handleClose()}>
                    Confirm close
                  </Button>
                  <Button variant="secondary" onClick={() => setClosingState('idle')}>Cancel</Button>
                </div>
              ) : closingState === 'closing' ? (
                <Button variant="danger" disabled>Closing…</Button>
              ) : (
                <Button
                  variant="danger"
                  disabled={!actualCash.trim()}
                  onClick={() => setClosingState('confirming')}
                >
                  <ClipboardList size={14} className="mr-1.5" />
                  Close Day
                </Button>
              )}
              {!actualCash.trim() && (
                <p className="mt-1 text-label text-fg-tertiary">Enter the actual cash in drawer to enable Close Day.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Shared UI ────────────────────────────────────────────────────────────────

function KPI({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', accent ? 'text-accent-ink' : 'text-fg-primary')}>
        {value}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

function PayRow({ label, amount, warn }: { label: string; amount: number; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className={cn('text-body', warn ? 'text-warning-ink' : 'text-fg-secondary')}>{label}</span>
      <span className={cn('text-body tabular-nums', warn ? 'text-warning-ink' : 'text-fg-primary')}>
        {formatCurrency(amount)}
      </span>
    </div>
  );
}

function RecRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className={cn('text-body text-fg-secondary', bold && 'font-semibold text-fg-primary')}>{label}</span>
      <span className={cn('text-body tabular-nums text-fg-primary', bold && 'font-semibold')}>{value}</span>
    </div>
  );
}
